/*
This is a special hook that loads in the options and mail message data
We can then run reply, reply-all- forward, filing, etc functions against them
I've seperated it from the personal emails part as code is duplicated all over the place here
*/

// TODO: I know this is duplicated but at least its contained in one file!
// Worked ~80hours this week and I just need this to work...

import _ from "lodash";
import toast from "react-hot-toast";
import { useAuth } from "react-oidc-context";
import { toNString } from "../helpers/guids";
import { openMailPopout } from "../components/modals/email-popout";
import { openFileEmailModal } from "../components/modals/email-file";
import { FiledMailMessage, MailMessageData, MailMessageRecipientData } from "../types/MailMessageData";
import { usePatchFiledMailMutation } from "../services/useremails";

export type MailMessageClientOptions = {
    masterAccountId: string
    projectId?: string | null | undefined
}

export type MailMessageControlOptions = {
    folderId?: string | null | undefined,
    searchTerm?: string | null | undefined
};

export type useMailMessageControlProps = {
    onForward: (clientOptions: MailMessageClientOptions | null) => Promise<MailMessage>
    onReply: (clientOptions: MailMessageClientOptions | null) => Promise<MailMessage>
    onReplyAll: (clientOptions: MailMessageClientOptions | null) => Promise<MailMessage>
};

export type useFiledMailMessageControlProps = useMailMessageControlProps & {
    assignToProject: (v: string) => Promise<boolean>
}

export type useFileableMailMessageControlProps = useMailMessageControlProps & {
    onFile: () => void
};

const buildRecipientsList = (v: MailMessageRecipientData[]) : MailMessageRecipient[] => {
    // flat-pack these email address values
    // how graph gives you them is a little odd
    const coreRecipients : { name: string, address: string }[] = v.map(e => ({
        name: e.emailAddress.name,
        address: e.emailAddress.address
    }));

    // make sure who we are sending too is unique
    const uniqRecipients = _.uniqBy(coreRecipients, 'address');
    return uniqRecipients.map((el: any) => ({
        contactName: el.name,
        contactEmailAddress: el.address,
        isNew: false,
        isRestricted: false
    }));
};

export const useFiledMailMessageControls = (
    mailMessage: FiledMailMessage,
    masterAccountId: string,
    options: MailMessageControlOptions | null = null
): useFiledMailMessageControlProps => {
    const { user } = useAuth();
    const [patchEmail, { isLoading }] = usePatchFiledMailMutation();

    const assignToProject = (v: string) : Promise<boolean> => new Promise((resolve, reject) => {
        return toast.promise(patchEmail({ graphId: mailMessage.graphId, projectId: v }).unwrap(), {
            loading: 'Updating project assigned to filed email.',
            success: () => {
                resolve(true);
                return `Project assigned.`
            },
            error: () => {
                resolve(false);
                return `Failed to update the project assigned to this filed email.`
            }
        });
    });

    const onForward = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('forward', {
            parentMailMessageId: mailMessage.graphId,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `FW: ${mailMessage.subject}`,
            body: '',
            from: toNString(user!.profile.sub),
            attachments: [],
            toRecipients: [],
            ccRecipients: [],
        });
    };

    const onReply = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply', {
            parentMailMessageId: mailMessage.graphId,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage.subject}`,
            body: '',
            from: toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList([{ emailAddress: { address: mailMessage.senderEmailAddress, name: mailMessage.senderEmailAddress } }]),
            ccRecipients: [],
            bccRecipients: []
        });
    };

    const onReplyAll = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply-all', {
            parentMailMessageId: mailMessage.graphId,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage.subject}`,
            body: '',
            from: toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList(mailMessage.recipientEmails.map(el => ({
                emailAddress: {
                    address: el,
                    name: el
                }
            }))),
            ccRecipients: [],
            bccRecipients: []
        })
    };

    return {
        assignToProject,
        onForward,
        onReply,
        onReplyAll
    };
}

const useMailMessageControls = (
    mailMessage: MailMessageData,
    delegate: UserDelegate | null = null,
    options: MailMessageControlOptions | null = null
) : useFileableMailMessageControlProps => {
    const { user } = useAuth();

    const onFile = () => {
        return openFileEmailModal(mailMessage, delegate, options);
    };

    const onForward = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('forward', {
            parentMailMessageId: mailMessage.id,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `FW: ${mailMessage.subject}`,
            body: '',
            from: delegate?.userId ?? toNString(user!.profile.sub),
            attachments: [],
            toRecipients: [],
            ccRecipients: [],
        });
    };

    const onReply = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply', {
            parentMailMessageId: mailMessage.id,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage.subject}`,
            body: '',
            from: delegate?.userId ?? toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList([mailMessage.from]),
            ccRecipients: [],
            bccRecipients: []
        });
    };

    const onReplyAll = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply-all', {
            parentMailMessageId: mailMessage.id,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage.subject}`,
            body: '',
            from: delegate?.userId ?? toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList([mailMessage.from, ...mailMessage.toRecipients]),
            ccRecipients: buildRecipientsList([...mailMessage.ccRecipients]),
            bccRecipients: buildRecipientsList([...mailMessage.bccRecipients])
        })
    };

    return {
        onFile,
        onForward,
        onReply,
        onReplyAll
    };
};

export default useMailMessageControls;